/**
 * Application root and routing (CLAUDE.md 13, 14).
 *
 * Provider order is load-bearing:
 *
 *   ErrorBoundary        catches everything below, including provider failures
 *     QueryClient        server state
 *       ThemeProvider    above auth, so the login page has a theme
 *         TooltipProvider
 *           ToastProvider
 *             Router
 *               AuthProvider   needs the router for redirects and the query client
 *                              for cache clearing on sign-out
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { AuthProvider } from '@/auth/AuthProvider'
import { PreferenceSync } from '@/auth/PreferenceSync'
import { ProtectedRoute, PublicOnlyRoute, SysadminRoute } from '@/auth/ProtectedRoute'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { ToastProvider } from '@/components/ui/toast'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider } from '@/theme/ThemeProvider'

import { AdminPage } from '@/pages/AdminPage'
import { ChangePasswordPage } from '@/pages/ChangePasswordPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { InvitationPage } from '@/pages/InvitationPage'
import { LoginPage } from '@/pages/LoginPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PositionPage } from '@/pages/PositionPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { ProjectPage } from '@/pages/ProjectPage'
import { HomePage } from '@/pages/HomePage'
import { ProjectsPage } from '@/pages/ProjectsPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { SharedPage } from '@/pages/SharedPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { SheetPage } from '@/pages/SheetPage'
import { SheetsPage } from '@/pages/SheetsPage'
import { SupportPage } from '@/pages/SupportPage'
import { SupportNewPage } from '@/pages/SupportNewPage'
import { SupportTicketPage } from '@/pages/SupportTicketPage'
import { AdminTicketPage } from '@/pages/AdminTicketPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // One retry: a calculation UI should surface a failure quickly rather than
      // appearing to hang while it tries three times.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider delayDuration={300}>
            <ToastProvider>
              <BrowserRouter>
                <AuthProvider>
                  <PreferenceSync />
                  <Routes>
                    {/* Public */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    {/* A share link is opened by someone with no account, so it must
                        not sit behind either guard -- not ProtectedRoute, which would
                        redirect them to a sign-in they cannot complete, nor
                        PublicOnlyRoute, which would bounce a signed-in engineer
                        checking their own link (CLAUDE.md 11.4). */}
                    <Route path="/s/:token" element={<SharedPage />} />
                    {/* An invitation is opened by someone who may have no account
                        yet, and may equally be opened by a colleague who is already
                        signed in. Both guards would be wrong, for the two reasons
                        above, so it sits beside the share route (CLAUDE.md 11.6). */}
                    <Route path="/invitations/:token" element={<InvitationPage />} />
                    {/* Confirmation links are followed from a mail client, where the
                        session is whatever that browser happens to hold. Guarding it
                        either way would strand half the recipients (CLAUDE.md 11.6). */}
                    <Route path="/verify-email" element={<VerifyEmailPage />} />

                    <Route element={<PublicOnlyRoute />}>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      {/* The reset link is followed by someone who cannot sign in, so
                          it must be reachable without a session. */}
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                    </Route>

                    {/* Authenticated */}
                    <Route element={<ProtectedRoute />}>
                      {/* Not a redirect to the project list any more: section 14.3
                          makes mobile review-first, and a checker opening the app had
                          nowhere to see what was waiting for them. */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/projects" element={<ProjectsPage />} />
                      <Route path="/projects/:projectId" element={<ProjectPage />} />
                      <Route path="/positions/:positionId" element={<PositionPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      {/* Ahead of the `:section` route: this is the one page
                          `ProtectedRoute` lets through while a password change is
                          owed, so it must not render the workspace chrome that a
                          blocked user would only see 403 from (section 19). */}
                      <Route path="/settings/password" element={<ChangePasswordPage />} />
                      <Route path="/settings/:section" element={<SettingsPage />} />
                      {/* Sheets are a separate section, never a peer of a
                          verified module in the catalog (section 1.3). */}
                      <Route path="/sheets" element={<SheetsPage />} />
                      <Route path="/sheets/:sheetId" element={<SheetPage />} />
                      <Route path="/support" element={<SupportPage />} />
                      <Route path="/support/new" element={<SupportNewPage />} />
                      <Route path="/support/:ticketId" element={<SupportTicketPage />} />
                    </Route>

                    {/* Server administration is a separate section for a separate
                        audience (section 12), so it sits outside the workspace routes
                        and behind its own guard. The API authorises independently --
                        this only decides what to render. */}
                    <Route element={<SysadminRoute />}>
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/admin/tickets/:ticketId" element={<AdminTicketPage />} />
                      <Route path="/admin/:section" element={<AdminPage />} />
                    </Route>

                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </AuthProvider>
              </BrowserRouter>
            </ToastProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
